import { useState, useRef, useEffect } from 'react'
import { Search, ChevronDown, X, Check, Loader2 } from 'lucide-react'

/**
 * SearchableSelect — combobox with inline search.
 *
 * Local mode  (default)   : filters options client-side from the `options` array.
 * Server mode             : pass `onSearchChange` — component calls it as user types,
 *                           parent handles fetching; pass `isLoading` to show spinner.
 *
 * Props:
 *  options          — [{ value, label, sublabel? }]
 *  value            — selected value (string | number)
 *  onChange         — (value) => void
 *  selectedOption   — { value, label, sublabel? }  — used to display the trigger label
 *                     when `value` is set but the item may not be in current `options`
 *                     (important in server mode after search clears)
 *  onSearchChange   — (term: string) => void  — enables server mode
 *  isLoading        — boolean  — shows spinner row (server mode)
 *  placeholder      — trigger placeholder
 *  searchPlaceholder
 *  disabled
 *  clearable
 *  maxHeight        — dropdown list max-height px (default 240)
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  selectedOption,
  onSearchChange,
  isLoading = false,
  placeholder = 'اختر...',
  searchPlaceholder = 'ابحث...',
  disabled = false,
  clearable = false,
  maxHeight = 240,
}) {
  const [open, setOpen]     = useState(false)
  const [search, setSearch] = useState('')
  const containerRef        = useRef(null)
  const searchRef           = useRef(null)

  const serverMode = typeof onSearchChange === 'function'

  // In local mode filter client-side; in server mode show all options as-is (API filtered)
  const displayed = serverMode
    ? options
    : search.trim()
      ? options.filter((o) =>
          o.label?.toLowerCase().includes(search.toLowerCase()) ||
          o.sublabel?.toLowerCase().includes(search.toLowerCase())
        )
      : options

  // Resolve the label to show in the trigger
  const resolvedSelected =
    selectedOption && String(selectedOption.value) === String(value)
      ? selectedOption
      : options.find((o) => String(o.value) === String(value))

  useEffect(() => {
    if (open) {
      setSearch('')
      if (serverMode) onSearchChange('')
      setTimeout(() => searchRef.current?.focus(), 60)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSearchInput = (e) => {
    const term = e.target.value
    setSearch(term)
    if (serverMode) onSearchChange(term)
  }

  const handleSelect = (opt) => {
    onChange(opt.value, opt)
    setOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange('', null)
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* ── Trigger ─────────────────────────────────────────────────────── */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderRadius: 12,
          background: disabled ? '#F3F3F3' : '#E2E2E2',
          border: open ? '1.5px solid #000666' : '1.5px solid transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontFamily: 'Cairo, sans-serif', fontSize: 13, fontWeight: 600,
          color: resolvedSelected ? '#1A1C1C' : '#767683',
          transition: 'border 0.15s', boxSizing: 'border-box',
          outline: 'none', textAlign: 'right',
        }}
      >
        <span style={{ flex: 1, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {resolvedSelected ? (
            <>
              {resolvedSelected.label}
              {resolvedSelected.sublabel && (
                <span style={{ color: '#767683', fontWeight: 400, fontSize: 11, marginRight: 6 }}>
                  {resolvedSelected.sublabel}
                </span>
              )}
            </>
          ) : placeholder}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {clearable && resolvedSelected && (
            <span
              onClick={handleClear}
              style={{ width: 18, height: 18, borderRadius: 6, background: '#C4C4C4', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <X size={10} style={{ color: '#fff' }} />
            </span>
          )}
          <ChevronDown
            size={14}
            style={{ color: '#767683', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
          />
        </span>
      </button>

      {/* ── Dropdown ────────────────────────────────────────────────────── */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, left: 0,
          background: '#fff', borderRadius: 14, zIndex: 200,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)', overflow: 'hidden',
          border: '1px solid rgba(0,6,102,0.1)',
        }}>
          {/* Search box */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #F3F3F3', position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', right: 22, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
            <input
              ref={searchRef}
              value={search}
              onChange={handleSearchInput}
              placeholder={searchPlaceholder}
              style={{
                width: '100%', border: 'none', background: '#F3F3F3',
                borderRadius: 8, padding: '6px 32px 6px 10px',
                fontSize: 12, fontFamily: 'Tajawal, sans-serif',
                color: '#1A1C1C', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Options list */}
          <div style={{ maxHeight, overflowY: 'auto' }}>
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 16, color: '#767683', fontSize: 12, fontFamily: 'Tajawal, sans-serif' }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                جارٍ التحميل...
              </div>
            ) : displayed.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#767683', fontSize: 12, fontFamily: 'Tajawal, sans-serif' }}>
                {search ? 'لا توجد نتائج' : 'لا توجد خيارات'}
              </div>
            ) : (
              displayed.map((opt) => {
                const isSelected = String(opt.value) === String(value)
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '9px 14px', border: 'none', cursor: 'pointer',
                      background: isSelected ? 'rgba(0,6,102,0.06)' : 'transparent',
                      textAlign: 'right', transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#F9F9F9' }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'Cairo, sans-serif', fontSize: 13, fontWeight: isSelected ? 700 : 500, color: isSelected ? '#000666' : '#1A1C1C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {opt.label}
                      </div>
                      {opt.sublabel && (
                        <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683', marginTop: 1 }}>
                          {opt.sublabel}
                        </div>
                      )}
                    </div>
                    {isSelected && <Check size={14} style={{ color: '#000666', flexShrink: 0 }} />}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
